import creditCardType from 'credit-card-type';

/**
 * Maps credit-card-type's `type` strings to the backend CardBrandEnum member
 * names the API expects (bound by name). Brands the gateway doesn't support are
 * intentionally absent — they resolve to '' so the UI can ask for confirmation.
 */
const TYPE_TO_BRAND = {
  'visa':             'Visa',
  'mastercard':       'MasterCard',
  'american-express': 'AmericanExpress',
  'elo':              'Elo',
  'hipercard':        'Hipercard',
  'diners-club':      'DinersClub',
  'maestro':          'Maestro'
};

/**
 * Returns the supported card brands that match the (partial) number, ordered by
 * credit-card-type's confidence, mapped to backend enum names and de-duplicated.
 *
 * credit-card-type narrows as more digits are typed: early on it may return
 * several candidates (e.g. Elo and Maestro share BIN ranges), collapsing to one
 * once the number is specific enough.
 *
 * @param {string} cardNumber raw or masked number
 * @returns {string[]} e.g. ['Visa'] or ['Maestro', 'Elo'] or []
 */
export function detectBrandCandidates(cardNumber) {
  const digits = String(cardNumber || '').replace(/\D/g, '');
  if (!digits) return [];

  const seen = new Set();
  const brands = [];
  for (const match of creditCardType(digits)) {
    const brand = TYPE_TO_BRAND[match.type];
    if (brand && !seen.has(brand)) {
      seen.add(brand);
      brands.push(brand);
    }
  }
  return brands;
}

/**
 * Best-guess single brand for the number, or '' when unknown/unsupported.
 *
 * @param {string} cardNumber
 * @returns {string} backend CardBrandEnum name, or ''
 */
export function detectBrand(cardNumber) {
  return detectBrandCandidates(cardNumber)[0] || '';
}

/** Human-readable label for a backend brand enum name. */
export function brandLabel(brand) {
  const labels = {
    Visa: 'Visa',
    MasterCard: 'Mastercard',
    AmericanExpress: 'American Express',
    Elo: 'Elo',
    Hipercard: 'Hipercard',
    DinersClub: 'Diners Club',
    Maestro: 'Maestro'
  };
  return labels[brand] || brand;
}

